import React from 'react'
import {
  ArrowRightOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  ShopOutlined
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  InputNumber,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import { Link } from 'react-router-dom'
import { GetProductModal } from 'components/GetProductModal'
import { logisticsSettingsApi } from 'api/logisticsSettings'
import { pickupPointsApi } from 'api/pickupPoints'
import { sellerApi } from 'api/seller'
import { yandexDeliveryApi } from 'api/yandexDelivery'
import {
  IDeliveryCalculationResponse
} from 'models/deliveryCalculator'
import { EDeliverySubsidyReason, ILogisticsSettings } from 'models/logisticsSettings'
import { IPickupPoint, TPickupPointProvider } from 'models/pickupPoint'
import { EProductStatus, IProductResponse } from 'models/product'
import { ISeller } from 'models/seller'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { showServerError } from 'shared/utils/showServerError'
import { showMessage } from 'shared/utils/showMessage'
import {
  distributeCheck,
  formatDeliveryPeriod,
  formatDeliveryWeight,
  getBasketDefaultTotal,
  getBasketOriginalTotal,
  getDeliveryPointAddress,
  getLineWeightKg,
  getPricePerKg,
  getProductPrice,
  getSubsidyDiagnostics,
  ICalculatorLine,
  toCalculationPvz
} from './DeliveryCalculator.utils'
import {
  DeliveryPickupPointSelector,
  getPickupPointProviderName
} from './DeliveryPickupPointSelector'
import styles from './Logistics.module.scss'

interface IDeliveryCalculatorProps {
  settings: ILogisticsSettings
}

type TOriginMode = 'seller' | 'manual'
type TSellerOrigins = Partial<Record<TPickupPointProvider, IPickupPoint>>

const DELIVERY_PROVIDER_LABELS: Record<TPickupPointProvider, string> = {
  CDEK: 'СДЭК',
  YANDEX: 'Яндекс / 5Post'
}

const POINT_VALIDATION_RADIUS = 0.02

const isSamePickupPoint = (left: IPickupPoint, right: IPickupPoint) =>
  left.provider === right.provider &&
  Boolean(
    (left.uuid && right.uuid && left.uuid === right.uuid) ||
    (left.code && right.code && left.code === right.code)
  )

const isPickupPointAvailable = async (
  point: IPickupPoint,
  weightKg: number,
  purpose: 'pickup' | 'dropoff'
) => {
  if (purpose === 'dropoff' && point.provider === 'YANDEX') {
    const { data } = await yandexDeliveryApi.getDropoffStations({
      pickup_point_ids: [point.uuid ?? point.code],
      available_for_dropoff: true
    })
    return data.some((candidate) =>
      isSamePickupPoint(point, toPickupPoint('YANDEX', candidate))
    )
  }

  const { latitude, longitude } = point.location
  const { data } = await pickupPointsApi.getPickupPoints({
    latitude: {
      from: latitude - POINT_VALIDATION_RADIUS,
      to: latitude + POINT_VALIDATION_RADIUS
    },
    longitude: {
      from: longitude - POINT_VALIDATION_RADIUS,
      to: longitude + POINT_VALIDATION_RADIUS
    },
    limit: 40,
    selection_strategy: 'nearest',
    ...(weightKg > 0 && { weight_max: weightKg })
  })
  const candidates = point.provider === 'CDEK' ? data.cdek : data.yandex
  return candidates.some((candidate) => isSamePickupPoint(point, candidate))
}

const toPickupPoint = (
  provider: IPickupPoint['provider'],
  point: {
    code: string
    uuid?: string
    name: string
    location: IPickupPoint['location']
    work_time?: string
  }
): IPickupPoint => ({
  provider,
  code: point.code,
  uuid: point.uuid,
  name: point.name,
  location: point.location,
  work_time: point.work_time
})

export const DeliveryCalculator: React.FC<IDeliveryCalculatorProps> = ({
  settings
}) => {
  const [sellers, setSellers] = React.useState<ISeller[]>([])
  const [isSellersLoading, setIsSellersLoading] = React.useState(false)
  const [sellerId, setSellerId] = React.useState<string>()
  const [lines, setLines] = React.useState<ICalculatorLine[]>([])
  const [isProductsOpen, setIsProductsOpen] = React.useState(false)
  const [checkTotal, setCheckTotal] = React.useState(0)
  const [isCheckManual, setIsCheckManual] = React.useState(false)
  const [destination, setDestination] = React.useState<IPickupPoint | null>(null)
  const [origin, setOrigin] = React.useState<IPickupPoint | null>(null)
  const [originMode, setOriginMode] = React.useState<TOriginMode>('seller')
  const [sellerOrigins, setSellerOrigins] = React.useState<TSellerOrigins>({})
  const [isSellerOriginsLoading, setIsSellerOriginsLoading] = React.useState(false)
  const [sellerOriginsError, setSellerOriginsError] = React.useState<string>()
  const [sellerOriginsReloadKey, setSellerOriginsReloadKey] = React.useState(0)
  const [result, setResult] = React.useState<IDeliveryCalculationResponse | null>(
    null
  )
  const [isResultStale, setIsResultStale] = React.useState(false)
  const [isCalculating, setIsCalculating] = React.useState(false)
  const [isRouteValidating, setIsRouteValidating] = React.useState(false)
  const [requestError, setRequestError] = React.useState<string>()
  const routeValidationIdRef = React.useRef(0)

  const invalidateResult = React.useCallback(() => {
    setIsResultStale(true)
    setRequestError(undefined)
  }, [])

  React.useEffect(() => {
    const load = async () => {
      setIsSellersLoading(true)
      try {
        const { data } = await sellerApi.getSearchSellers(true)
        setSellers(data.filter((seller) => seller.has_published_products))
      } catch (error) {
        showServerError(error)
      } finally {
        setIsSellersLoading(false)
      }
    }
    load()
  }, [])

  React.useEffect(() => {
    if (!sellerId) {
      setSellerOrigins({})
      setSellerOriginsError(undefined)
      setIsSellerOriginsLoading(false)
      return
    }

    let cancelled = false
    const load = async () => {
      setIsSellerOriginsLoading(true)
      setSellerOriginsError(undefined)
      const nextOrigins: TSellerOrigins = {}
      let hasLookupError = false

      const [cdekResult, sellerResult] = await Promise.allSettled([
        sellerApi.getSelectedDeliveryPoint(sellerId),
        sellerApi.getSeller(sellerId)
      ])

      if (cdekResult.status === 'fulfilled' && cdekResult.value.data?.code) {
        nextOrigins.CDEK = toPickupPoint('CDEK', cdekResult.value.data)
      } else if (cdekResult.status === 'rejected') {
        hasLookupError = true
      }

      if (sellerResult.status === 'fulfilled') {
        const seller = sellerResult.value.data
        const stationId =
          seller.yandex_dropoff_station_id ??
          seller.logistics?.yandex_dropoff_station_id
        if (stationId) {
          try {
            const { data: stations } = await yandexDeliveryApi.getDropoffStations({
              pickup_point_ids: [stationId]
            })
            const station = stations[0]
            if (station) {
              nextOrigins.YANDEX = toPickupPoint('YANDEX', station)
            }
          } catch {
            hasLookupError = true
          }
        }
      } else {
        hasLookupError = true
      }

      if (!cancelled) {
        setSellerOrigins(nextOrigins)
        setSellerOriginsError(
          hasLookupError
            ? 'Не удалось проверить все пункты отправления продавца'
            : undefined
        )
        setIsSellerOriginsLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [sellerId, sellerOriginsReloadKey])

  React.useEffect(() => {
    if (originMode !== 'seller') return

    const origins = Object.values(sellerOrigins).filter(Boolean) as IPickupPoint[]
    if (origins.length === 1) {
      setOrigin(origins[0])
      return
    }

    setOrigin((current) =>
      current && sellerOrigins[current.provider] ? sellerOrigins[current.provider]! : null
    )
  }, [originMode, sellerOrigins])

  const onSellerChange = (value?: string) => {
    routeValidationIdRef.current += 1
    setIsRouteValidating(false)
    setSellerId(value)
    setLines([])
    setCheckTotal(0)
    setIsCheckManual(false)
    setDestination(null)
    setOrigin(null)
    setOriginMode('seller')
    setSellerOrigins({})
    setSellerOriginsError(undefined)
    setIsSellerOriginsLoading(Boolean(value))
    setResult(null)
    setIsResultStale(false)
    setRequestError(undefined)
  }

  const updateLines = (next: ICalculatorLine[]) => {
    setLines(next)
    if (!isCheckManual) setCheckTotal(getBasketDefaultTotal(next))
    invalidateResult()

    if (!origin && !destination) return
    const validationId = ++routeValidationIdRef.current
    const weightKg = next.reduce(
      (sum, line) => sum + getLineWeightKg(line.product, line.count),
      0
    )
    const checkedOrigin = origin
    const checkedDestination = destination
    setIsRouteValidating(true)

    void Promise.allSettled([
      checkedOrigin
        ? isPickupPointAvailable(checkedOrigin, weightKg, 'dropoff')
        : Promise.resolve(true),
      checkedDestination
        ? isPickupPointAvailable(checkedDestination, weightKg, 'pickup')
        : Promise.resolve(true)
    ]).then(([originResult, destinationResult]) => {
      if (validationId !== routeValidationIdRef.current) return
      setIsRouteValidating(false)

      if (originResult.status === 'rejected' || destinationResult.status === 'rejected') {
        showMessage(
          'warning',
          'Не удалось перепроверить выбранные ПВЗ',
          'Пункты сохранены. Повторите изменение или выберите их заново перед расчётом.'
        )
        return
      }

      if (!originResult.value && checkedOrigin) {
        setOrigin(null)
        setDestination(null)
        showMessage(
          'warning',
          'Пункт отправления сброшен',
          `Пункт «${checkedOrigin.name}» недоступен для общего веса ${Number(weightKg.toFixed(3))} кг. ПВЗ получателя также нужно выбрать заново.`
        )
        return
      }

      if (!destinationResult.value && checkedDestination) {
        setDestination(null)
        showMessage(
          'warning',
          'ПВЗ получателя сброшен',
          `Пункт «${checkedDestination.name}» недоступен для общего веса ${Number(weightKg.toFixed(3))} кг. Выберите другой ПВЗ.`
        )
      }
    })
  }

  const onProductsSave = (products: IProductResponse[]) => {
    const previous = new Map(lines.map((line) => [line.product._id, line]))
    updateLines(
      products.map((product) => previous.get(product._id) ?? { product, count: 1 })
    )
  }

  const onDestinationChange = React.useCallback((point: IPickupPoint | null) => {
    if (point && point.provider !== origin?.provider) return
    routeValidationIdRef.current += 1
    setIsRouteValidating(false)
    setDestination(point)
    invalidateResult()
  }, [invalidateResult, origin?.provider])

  const onOriginModeChange = (mode: TOriginMode) => {
    routeValidationIdRef.current += 1
    setIsRouteValidating(false)
    setOriginMode(mode)
    setOrigin(null)
    setDestination(null)
    invalidateResult()
  }

  const onOriginChange = React.useCallback((point: IPickupPoint | null) => {
    routeValidationIdRef.current += 1
    setIsRouteValidating(false)
    setOrigin(point)
    setDestination(null)
    invalidateResult()
  }, [invalidateResult])

  const onSellerOriginChange = (provider: TPickupPointProvider) => {
    routeValidationIdRef.current += 1
    setIsRouteValidating(false)
    setOrigin(sellerOrigins[provider] ?? null)
    setDestination(null)
    invalidateResult()
  }

  const totalWeightKg = lines.reduce(
    (sum, line) => sum + getLineWeightKg(line.product, line.count),
    0
  )
  const defaultTotal = getBasketDefaultTotal(lines)
  const originalTotal = getBasketOriginalTotal(lines)
  const distributedPrices = distributeCheck(lines, checkTotal)
  const availableProviders = Object.keys(sellerOrigins) as TPickupPointProvider[]
  const columns: ColumnsType<ICalculatorLine> = [
    {
      title: 'Товар',
      key: 'product',
      render: (_, line) => (
        <div className={styles.calculatorProductName}>
          <Typography.Text strong>{line.product.name}</Typography.Text>
          <Typography.Text type='secondary'>
            Артикул: {line.product.article || '—'}
          </Typography.Text>
        </div>
      )
    },
    {
      title: 'Количество',
      dataIndex: 'count',
      width: 125,
      render: (_, line) => (
        <InputNumber
          min={1}
          precision={0}
          value={line.count}
          aria-label={`Количество ${line.product.name}`}
          onChange={(value) =>
            updateLines(
              lines.map((item) =>
                item.product._id === line.product._id
                  ? { ...item, count: Number(value ?? 1) }
                  : item
              )
            )
          }
        />
      )
    },
    {
      title: 'Цена',
      key: 'price',
      width: 150,
      render: (_, line) => (
        <div className={styles.calculatorProductName}>
          <Typography.Text>
            {getNormalizedPrice(Number(line.product.price ?? 0) * line.count)}
          </Typography.Text>
          <Typography.Text type='secondary'>
            после скидки {getNormalizedPrice(getProductPrice(line.product) * line.count)}
          </Typography.Text>
        </div>
      )
    },
    {
      title: 'Вес',
      key: 'weight',
      width: 110,
      render: (_, line) => `${Number(getLineWeightKg(line.product, line.count).toFixed(3))} кг`
    },
    {
      title: '',
      key: 'delete',
      width: 48,
      render: (_, line) => (
        <Button
          type='text'
          danger
          aria-label={`Удалить ${line.product.name}`}
          icon={<DeleteOutlined />}
          onClick={() =>
            updateLines(
              lines.filter((item) => item.product._id !== line.product._id)
            )
          }
        />
      )
    }
  ]

  const canCalculate =
    !!sellerId &&
    !!lines.length &&
    checkTotal >= 0 &&
    !!destination &&
    !!origin &&
    !isRouteValidating &&
    origin.provider === destination.provider

  const calculate = async () => {
    if (!sellerId || !destination || !origin || !canCalculate) return
    setIsCalculating(true)
    setRequestError(undefined)
    try {
      const { data } = await logisticsSettingsApi.calculateDelivery({
        to_pvz: toCalculationPvz(destination),
        orders: [
          {
            seller_id: sellerId,
            from_pvz: toCalculationPvz(origin),
            items: lines.map((line, index) => ({
              product_id: line.product._id,
              count: line.count,
              discounted_price: distributedPrices[index]
            }))
          }
        ]
      })
      setResult(data)
      setIsResultStale(false)
    } catch (error) {
      setRequestError('Не удалось рассчитать доставку. Проверьте параметры и повторите запрос.')
      showServerError(error)
    } finally {
      setIsCalculating(false)
    }
  }

  const sellerResult = result?.seller_deliveries[0]
  const costSharing = sellerResult?.calculation.cost_sharing
  const deliveryReason = sellerResult?.logistics_settings.reason || costSharing?.reason
  const appliedRule = sellerResult?.logistics_settings.applied_rule ?? null
  const diagnostics = sellerResult
    ? getSubsidyDiagnostics(
      sellerResult.cart.seller_commission,
      sellerResult.calculation.provider_cost,
      appliedRule,
      settings.platform_commission_subsidy_limit_percent
    )
    : null

  return (
    <div className={styles.calculatorLayout}>
      <div className={styles.calculatorForm}>
        <Card title='1. Продавец' className={styles.policyCard}>
          <Typography.Text type='secondary' className={styles.cardHint}>
            Сначала выберите продавца. Следующим шагом сформируйте корзину,
            чтобы карты учитывали общий вес товаров.
          </Typography.Text>
          <Select
            showSearch
            allowClear
            optionFilterProp='label'
            loading={isSellersLoading}
            value={sellerId}
            placeholder='Выберите продавца'
            className={styles.fullWidth}
            onChange={onSellerChange}
            options={sellers.map((seller) => ({
              value: seller._id,
              label: seller.shop_details?.shop_name || seller._id
            }))}
          />
        </Card>

        {sellerId && (
          <Card title='2. Товары и чек' className={styles.policyCard}>
            <Typography.Text type='secondary' className={styles.cardHint}>
                В расчёт можно добавить несколько опубликованных товаров выбранного продавца.
            </Typography.Text>
            <div className={styles.calculatorSectionHead}>
              <Typography.Text strong>Корзина</Typography.Text>
              <Button
                icon={<PlusOutlined />}
                onClick={() => setIsProductsOpen(true)}
              >
                  Выбрать товары
              </Button>
            </div>
            {lines.length ? (
              <Table
                rowKey={(line) => line.product._id}
                columns={columns}
                dataSource={lines}
                pagination={false}
                size='small'
                scroll={{ x: 720 }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description='Товары не выбраны'
              />
            )}
            <div className={styles.calculatorTotals}>
              <div>
                <Typography.Text type='secondary'>Исходная сумма</Typography.Text>
                <Typography.Text>{getNormalizedPrice(originalTotal)}</Typography.Text>
              </div>
              <div>
                <Typography.Text type='secondary'>После скидок</Typography.Text>
                <Typography.Text>{getNormalizedPrice(defaultTotal)}</Typography.Text>
              </div>
              <div>
                <Typography.Text type='secondary'>Общий вес</Typography.Text>
                <Typography.Text>{Number(totalWeightKg.toFixed(3))} кг</Typography.Text>
              </div>
            </div>
            <div className={styles.checkField}>
              <div>
                <Typography.Text strong>Чек для расчёта</Typography.Text>
                <Typography.Text type='secondary'>
                    Сумма распределяется по строкам пропорционально цене после скидки.
                </Typography.Text>
              </div>
              <Space>
                <InputNumber
                  min={0}
                  precision={2}
                  value={checkTotal}
                  addonAfter='₽'
                  aria-label='Чек для расчёта'
                  onChange={(value) => {
                    setCheckTotal(Number(value ?? 0))
                    setIsCheckManual(true)
                    invalidateResult()
                  }}
                />
                {isCheckManual && (
                  <Button
                    type='link'
                    onClick={() => {
                      setCheckTotal(defaultTotal)
                      setIsCheckManual(false)
                      invalidateResult()
                    }}
                  >
                      Вернуть сумму товаров
                  </Button>
                )}
              </Space>
            </div>
          </Card>
        )}

        {sellerId && lines.length > 0 && (
          <Card title='3. Пункт отправления' className={styles.policyCard}>
            <div>
              {isSellerOriginsLoading ? (
                <Space>
                  <Spin />
                  <Typography.Text type='secondary'>
                    Загружаем пункты отправления продавца
                  </Typography.Text>
                </Space>
              ) : availableProviders.length === 0 ? (
                <Alert
                  type='warning'
                  showIcon
                  message={sellerOriginsError ?? 'У продавца нет доступного пункта отправления'}
                  description={sellerOriginsError
                    ? 'Повторите проверку. Если ошибка сохранится, проверьте настройки продавца.'
                    : 'Настройте ПВЗ СДЭК или пункт сдачи Яндекс/5Post, затем повторите проверку.'}
                  action={(
                    <Space direction='vertical' size={4}>
                      <Button
                        size='small'
                        onClick={() => setSellerOriginsReloadKey((key) => key + 1)}
                      >
                        Проверить снова
                      </Button>
                      <Link to={getSellerEditPath(sellerId)} target='_blank'>
                        Настройки продавца
                      </Link>
                    </Space>
                  )}
                />
              ) : (
                <>
                  <div className={styles.originMode}>
                    <div>
                      <Typography.Text strong>Пункт отправления</Typography.Text>
                      <br />
                      <Typography.Text type='secondary'>
                        Сохранённый пункт продавца можно заменить вручную.
                      </Typography.Text>
                    </div>
                    <Radio.Group
                      value={originMode}
                      optionType='button'
                      buttonStyle='solid'
                      onChange={(event) => onOriginModeChange(event.target.value)}
                      options={[
                        { label: 'Пункт продавца', value: 'seller' },
                        { label: 'Выбрать на карте', value: 'manual' }
                      ]}
                    />
                  </div>

                  {sellerOriginsError && (
                    <Alert
                      className={styles.sellerDeliveryWarning}
                      type='warning'
                      showIcon
                      message={sellerOriginsError}
                      description='Часть настроек получить не удалось. Доступные пункты показаны ниже.'
                      action={(
                        <Button
                          size='small'
                          onClick={() => setSellerOriginsReloadKey((key) => key + 1)}
                        >
                          Повторить
                        </Button>
                      )}
                    />
                  )}

                  {originMode === 'seller' && (
                    <>
                      <Radio.Group
                        className={styles.sellerOriginChoice}
                        value={origin?.provider}
                        onChange={(event) => onSellerOriginChange(event.target.value)}
                        options={availableProviders.map((provider) => ({
                          value: provider,
                          label: DELIVERY_PROVIDER_LABELS[provider]
                        }))}
                        optionType='button'
                        buttonStyle='solid'
                      />
                      {!origin && (
                        <Typography.Text type='secondary'>
                          Выберите сеть пункта отправления продавца.
                        </Typography.Text>
                      )}
                    </>
                  )}

                  {originMode === 'manual' && (
                    <DeliveryPickupPointSelector
                      value={origin}
                      onChange={onOriginChange}
                      weightMaxKg={totalWeightKg || undefined}
                      mapId='delivery-calculator-origin-map'
                      allowedProviders={availableProviders}
                      cityPlaceholder='Введите город отправления'
                      ariaLabel='Выбор пункта отправления'
                      pointPurpose='dropoff'
                    />
                  )}

                  {origin && (
                    <section
                      className={styles.selectedOrigin}
                      aria-label='Выбранный пункт отправления'
                    >
                      <span className={styles.routePointIcon}>
                        <ShopOutlined />
                      </span>
                      <div className={styles.routePointContent}>
                        <Typography.Text strong className={styles.routePointName}>
                          {origin.name || 'Пункт отправления продавца'}
                        </Typography.Text>
                        <Typography.Text
                          type='secondary'
                          className={styles.routePointAddress}
                        >
                          {getDeliveryPointAddress(origin)}
                        </Typography.Text>
                      </div>
                      <Tag color='blue'>{getPickupPointProviderName(origin)}</Tag>
                    </section>
                  )}
                </>
              )}
            </div>

          </Card>
        )}

        {lines.length > 0 && origin && (
          <Card title='4. Пункт выдачи получателя' className={styles.policyCard}>
            <div className={styles.sellerDeliveryAvailability}>
              <div>
                <Typography.Text strong>
                  Пункты {DELIVERY_PROVIDER_LABELS[origin.provider]}
                </Typography.Text>
                <Typography.Text type='secondary'>
                  На карте показана только сеть выбранного пункта отправления.
                </Typography.Text>
              </div>
              <Tag color='blue'>{DELIVERY_PROVIDER_LABELS[origin.provider]}</Tag>
            </div>
            <DeliveryPickupPointSelector
              value={destination}
              onChange={onDestinationChange}
              weightMaxKg={totalWeightKg || undefined}
              mapId='delivery-calculator-destination-map'
              allowedProviders={[origin.provider]}
            />

            {destination && (
              <section className={styles.routePreview} aria-label='Маршрут доставки'>
                <div className={styles.routePreviewHeader}>
                  <div>
                    <Typography.Text strong>Маршрут расчёта</Typography.Text>
                    <Typography.Text type='secondary'>
                      Доставка будет рассчитана между выбранными пунктами.
                    </Typography.Text>
                  </div>
                  <Tag color='blue' className={styles.routeProvider}>
                    {getPickupPointProviderName(destination)}
                  </Tag>
                </div>

                <div className={styles.routePoints}>
                  <div className={styles.routePoint}>
                    <span className={styles.routePointIcon}>
                      <ShopOutlined />
                    </span>
                    <div className={styles.routePointContent}>
                      <span className={styles.routePointLabel}>Откуда</span>
                      <Typography.Text strong className={styles.routePointName}>
                        {origin.name || 'Пункт отправления продавца'}
                      </Typography.Text>
                      <Typography.Text
                        type='secondary'
                        className={styles.routePointAddress}
                      >
                        {getDeliveryPointAddress(origin)}
                      </Typography.Text>
                    </div>
                  </div>

                  <ArrowRightOutlined className={styles.routeArrow} />

                  <div className={styles.routePoint}>
                    <span
                      className={[
                        styles.routePointIcon,
                        styles.routePointIconDestination
                      ].join(' ')}
                    >
                      <EnvironmentOutlined />
                    </span>
                    <div className={styles.routePointContent}>
                      <span className={styles.routePointLabel}>Куда</span>
                      <Typography.Text strong className={styles.routePointName}>
                        {destination.name || 'Пункт выдачи получателя'}
                      </Typography.Text>
                      <Typography.Text
                        type='secondary'
                        className={styles.routePointAddress}
                      >
                        {getDeliveryPointAddress(destination)}
                      </Typography.Text>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </Card>
        )}

        {isRouteValidating && (
          <Alert
            type='info'
            showIcon
            message='Проверяем доступность выбранных ПВЗ для нового веса'
          />
        )}
        {requestError && <Alert type='error' showIcon message={requestError} />}
        <Button
          type='primary'
          size='large'
          block
          disabled={!canCalculate}
          loading={isCalculating}
          onClick={calculate}
        >
          Рассчитать доставку
        </Button>
      </div>

      <section className={styles.calculatorResult}>
        <Card
          title='Результат расчёта'
          className={styles.policyCard}
          extra={result && (
            <Space>
              <Tag color={result.logistics_settings.mode === 'MATRIX' ? 'green' : 'default'}>
                {result.logistics_settings.mode}
              </Tag>
              <Tag>
                Политика · версия {result.logistics_settings.policy_version}
              </Tag>
            </Space>
          )}
        >
          {!result ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='Заполните параметры и запустите расчёт'
            />
          ) : (
            <Space direction='vertical' size={16} className={styles.fullWidth}>
              {isResultStale && (
                <Alert
                  type='warning'
                  showIcon
                  message='Параметры изменились — результат устарел'
                />
              )}
              <div className={styles.moneyCards}>
                {[
                  ['Перевозчик', result.calculation.provider_cost],
                  ['Покупатель', result.calculation.buyer_delivery_cost],
                  ['Субсидия площадки', result.calculation.platform_delivery_cost]
                ].map(([label, value]) => (
                  <div key={String(label)}>
                    <Typography.Text type='secondary'>{label}</Typography.Text>
                    <Typography.Title level={4}>
                      {getNormalizedPrice(Number(value))}
                      {label === 'Субсидия площадки' && appliedRule && (
                        <Typography.Text className={styles.subsidyPercent}>
                          {' '}({appliedRule.commission_compensation_percent}% субсидии от комиссии продавца)
                        </Typography.Text>
                      )}
                    </Typography.Title>
                  </div>
                ))}
              </div>
              <Descriptions
                size='small'
                bordered
                column={{ xs: 1, sm: 2, lg: 3 }}
              >
                <Descriptions.Item label='Срок доставки'>
                  {formatDeliveryPeriod(
                    sellerResult?.calculation.period_min,
                    sellerResult?.calculation.period_max
                  )}
                </Descriptions.Item>
                <Descriptions.Item label='Причина'>
                  {deliveryReason === EDeliverySubsidyReason.FREE_BY_DELIVERY_COST_LIMIT
                    ? 'Бесплатная доставка по лимиту'
                    : deliveryReason || '—'}
                </Descriptions.Item>
              </Descriptions>

              <section
                className={styles.appliedRuleBlock}
                aria-label='Сработавшее правило'
              >
                <Typography.Title level={5}>Сработавшее правило</Typography.Title>
                {appliedRule ? (
                  <Descriptions
                    size='small'
                    bordered
                    column={{ xs: 1, sm: 2, lg: 3 }}
                  >
                    <Descriptions.Item label='Ключ' span={3}>
                      <Typography.Text code>{appliedRule.key}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='Сумма заказа'>
                      {getNormalizedPrice(appliedRule.minimum_order_sum)}
                    </Descriptions.Item>
                    <Descriptions.Item label='Диапазон веса'>
                      {appliedRule.weight_from_kg}–{appliedRule.weight_to_kg} кг
                    </Descriptions.Item>
                    <Descriptions.Item label='Компенсация комиссии'>
                      {appliedRule.commission_compensation_percent}%
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <Alert
                    type='info'
                    showIcon
                    message={result.logistics_settings.mode === 'LEGACY'
                      ? 'Матрица отключена — использован старый расчёт'
                      : 'Матрица включена, но подходящее правило не найдено'}
                  />
                )}
              </section>

              {sellerResult && (
                <>
                  <Typography.Title level={5}>Чек и комиссия</Typography.Title>
                  <Descriptions
                    size='small'
                    bordered
                    column={{ xs: 1, sm: 2, lg: 3 }}
                  >
                    <Descriptions.Item label='Сумма'>
                      {getNormalizedPrice(sellerResult.cart.order_sum)}
                    </Descriptions.Item>
                    <Descriptions.Item label='Исходная сумма'>
                      {getNormalizedPrice(sellerResult.cart.original_order_sum)}
                    </Descriptions.Item>
                    <Descriptions.Item label='Вес'>
                      {formatDeliveryWeight(sellerResult.cart.order_weight_grams)}
                    </Descriptions.Item>
                    <Descriptions.Item label='Комиссия'>
                      {getNormalizedPrice(sellerResult.cart.seller_commission)} ({sellerResult.cart.commission_rate}%)
                    </Descriptions.Item>
                    <Descriptions.Item label='Штрафная надбавка'>
                      {sellerResult.cart.commission_penalty_rate}%
                    </Descriptions.Item>
                  </Descriptions>
                </>
              )}

              {diagnostics && (
                <>
                  <Typography.Title level={5}>Лимиты субсидии</Typography.Title>
                  <Descriptions
                    size='small'
                    bordered
                    column={{ xs: 1, sm: 2, lg: 2 }}
                  >
                    <Descriptions.Item label='Допустимо по правилу'>
                      {diagnostics.ruleAllowance == null
                        ? '—'
                        : getNormalizedPrice(diagnostics.ruleAllowance)}
                    </Descriptions.Item>
                    <Descriptions.Item label='Допустимо по политике'>
                      {getNormalizedPrice(diagnostics.policyAllowance)} ({settings.platform_commission_subsidy_limit_percent}%)
                    </Descriptions.Item>
                    <Descriptions.Item label='Процентный лимит'>
                      <Tag color={diagnostics.percentLimitApplied ? 'orange' : 'green'}>
                        {diagnostics.percentLimitApplied ? 'Сработал' : 'Не сработал'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label='Лимит стоимости доставки'>
                      <Tag color={diagnostics.deliveryCostLimitApplied ? 'orange' : 'green'}>
                        {diagnostics.deliveryCostLimitApplied ? 'Сработал' : 'Не сработал'}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </>
              )}

              {!!sellerResult?.cart.items.length && (
                <>
                  <Typography.Title level={5}>Позиции</Typography.Title>
                  <Table
                    size='small'
                    pagination={false}
                    rowKey={(item) => item._id ?? item.product_id}
                    dataSource={sellerResult.cart.items}
                    scroll={{ x: 620 }}
                    columns={[
                      {
                        title: 'Товар',
                        dataIndex: 'product_id',
                        render: (productId: string) =>
                          lines.find((line) => line.product._id === productId)?.product.name ?? productId
                      },
                      { title: 'Кол-во', dataIndex: 'count', width: 80 },
                      {
                        title: 'После скидки',
                        dataIndex: 'discounted_price',
                        render: (value: number) => getNormalizedPrice(value)
                      },
                      {
                        title: 'Вес',
                        render: (_, item) => `${Number(getLineWeightKg(item, item.count).toFixed(3))} кг`
                      },
                      {
                        title: 'Цена/кг',
                        render: (_, item) => {
                          const value = getPricePerKg(
                            item.discounted_price,
                            getLineWeightKg(item, item.count)
                          )
                          return value == null ? '—' : getNormalizedPrice(value)
                        }
                      }
                    ]}
                  />
                </>
              )}
            </Space>
          )}
        </Card>
      </section>

      {sellerId && (
        <GetProductModal<IProductResponse>
          visible={isProductsOpen}
          onClose={() => setIsProductsOpen(false)}
          onSave={(products) => onProductsSave(products as IProductResponse[])}
          title='Товары для расчёта доставки'
          saveText='Добавить в расчёт'
          initialSelected={lines.map((line) => line.product)}
          fixedSellerId={sellerId}
          fixedStatus={EProductStatus.PUBLISHED}
        />
      )}
    </div>
  )
}
